import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Avatar, Rate } from 'antd';
import Icon from 'assets/Icons';
import CashBtnV2 from 'assets/Icons/cashBtnV2';
import IcApt from 'assets/Icons/IcApt';
import IcNext from 'assets/Icons/IcNext';
import IcPreOrder from 'assets/Icons/IcPreOrder';
import Turn from 'assets/Icons/turn';
import Box from 'components/Box';
import Text from 'components/Text';
import colorTheme from 'constants/color';
import { EStaffListStatus, STAFF_STATUS_CONFIG } from 'features/user/services/constants';
import { get } from 'lodash';
import { IStaffAvailableItem } from 'services/shop/types/staff';
import styled from 'styled-components';

const getStaffName = (str: string) => {
  if (!str) return '';
  if (str?.length < 8) return str;
  return str.slice(0, 6) + '...';
};

interface StaffListItemExpandedProps {
  data: IStaffAvailableItem;
  onVoiceCall?: () => void;
  onChangeStatus?: () => void;
  staffClick?: () => void;
}

const StaffListItemExpanded = ({ data, onVoiceCall, onChangeStatus, staffClick }: StaffListItemExpandedProps) => {
  const statusWorking = data?.statusWorking || '';

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: data.staffId,
    data: {
      staff: data,
    },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
  };

  return (
    <StaffStyled
      className='staff-list-item'
      onClick={staffClick}
      ref={setNodeRef}
      style={{
        ...style,
        opacity: isDragging ? 0.5 : 1,
      }}
      {...attributes}
      {...listeners}
      $color={STAFF_STATUS_CONFIG[statusWorking as EStaffListStatus]?.bgColor || '#ccc'}
    >
      <Box display='flex' flexDirection='column' justifyContent='center' className='info-div'>
        <Box display='flex' justifyContent='space-between'>
          <Box display='flex' gap='2' alignItems='center'>
            <IcPreOrder />
            <Box display='flex' gap='3'>
              <Avatar
                className='circle-avatar'
                src={data?.staffAvatar || 'fail'}
                style={{ backgroundColor: 'rgb(245 106 0 / 30%)', border: '1px solid #f56a00' }}
              >
                {get(data?.staffName, [0], '')}
              </Avatar>
              <Box display='flex' gap='3'>
                <Box flexDirection='column' display='flex'>
                  <Text variant='H5' style={{ lineHeight: '1.2' }}>
                    {getStaffName(data?.staffName)}
                  </Text>
                  <Rate allowHalf disabled style={{ fontSize: 16 }} value={data?.stars || 0} />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box display='flex' alignItems='center' gap='3'>
            {data?.nextTimeAppointment?.trim() ? (
              <Box display='flex' gap='1' alignItems='start'>
                <Text variant='H8' style={{ fontWeight: 500 }} color='text_3_v2'>
                  Next Appointment:
                </Text>
                <Text variant='H8' style={{ fontWeight: 500 }} color='v2_orange_5'>
                  {data?.nextTimeAppointment}
                </Text>
              </Box>
            ) : (
              <Text variant='H8' style={{ fontWeight: 500 }} color='text_3_v2'>
                No upcoming appt
              </Text>
            )}
            <StatusBadge
              style={{
                backgroundColor: STAFF_STATUS_CONFIG[statusWorking as EStaffListStatus]?.bgColor || '#ccc',
                boxShadow: STAFF_STATUS_CONFIG[statusWorking as EStaffListStatus]?.boxShadow || 'none',
              }}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onChangeStatus?.();
              }}
            >
              <Text variant='H9' color='text_1' style={{ lineHeight: '1' }}>
                {STAFF_STATUS_CONFIG[statusWorking as EStaffListStatus]?.label || 'Unknown'}
              </Text>
              <IcNext width={20} height={20} />
            </StatusBadge>
            <button onClick={(e) => [e.stopPropagation(), onVoiceCall?.()]} className='btn-call-staff'>
              <Icon type='callStaff' />
            </button>
          </Box>
        </Box>
        <NextAppointment>
          <Box display='flex' gap='4' alignItems='center' pr='2'>
            <Text variant='H8' style={{ fontWeight: 500 }} color='v2_blue_3'>
              Last Service: {data?.lastTimeService?.trim() ? data.lastTimeService : '--'}
            </Text>
            <Text variant='H8' style={{ fontWeight: 500 }} color='text_3_v2'>
              C: {data?.timeClockIn?.trim() ? data.timeClockIn : '--'}
            </Text>
          </Box>
          <div className='info-item'>
            <IcApt fill={colorTheme.v2_blue_3} width={24} height={24} />
            <Box display='flex' alignItems='center' gap='1'>
              <Text variant='H5' color='v2_blue_3'>
                {data?.totalAppointment || 0}
              </Text>
              <Text variant='H8' style={{ fontWeight: 500 }}>
                Appt
              </Text>
            </Box>
          </div>
          <div className='info-item'>
            <Turn fill={colorTheme.v2_green_7} width={24} height={24} />
            <Box display='flex' alignItems='center' gap='1'>
              <Text variant='H5' color='v2_green_7'>
                {data?.turn || 0}
              </Text>
              <Text variant='H8' style={{ fontWeight: 500 }}>
                Turn
              </Text>
            </Box>
          </div>
          <div className='info-item'>
            <CashBtnV2 fill={colorTheme.v2_orange_5} width={24} height={24} />
            <Box display='flex' alignItems='center' gap='1'>
              <Text variant='H5' color='v2_orange_5'>
                {data?.bonus || 0}
              </Text>
              <Text variant='H8' style={{ fontWeight: 500 }}>
                Bonus
              </Text>
            </Box>
          </div>
        </NextAppointment>
      </Box>
    </StaffStyled>
  );
};

export default StaffListItemExpanded;

const StaffStyled = styled.div<{ $color: string }>`
  padding: 8px 8px 4px 8px;
  display: flex;
  background: #fff;
  border-radius: 10px;
  border-left: 4px solid ${({ $color }) => $color};
  transition: all 0.3s ease;
  gap: 16px;
  font-family: Poppins;

  &:hover {
    box-shadow: 0px 2px 4px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -3px 0px #D6D6E7 inset;
  }

  .ant-rate .ant-rate-star:not(:last-child) {
    margin-inline-end: 0;
  }
  .circle-avatar {
    height: 55px;
    width: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
    .ant-avatar-string {
      color: #fff;
      font-family: Poppins;
      font-size: 30px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
  }

  .info-div {
    flex: 1;
  }

  .btn-call-staff {
    display: inline-flex;
    justify-content: flex-end;
    padding-bottom: 4px;
    &:active .CallStaffIcon {
      background: #eee;
    }

    .icon-check-box-empty {
      border: 2px solid #86909c;
      background: #f5f5f5;
      border-radius: 8px;
      width: 45px;
      height: 45px;
    }
  }
`;

const NextAppointment = styled.div`
  display: flex;
  border-top: 1px solid ${colorTheme.v2_border_2};
  font-family: Poppins;

  .info-item {
    flex: 1;
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 0px 8px;
    border-left: 1px solid ${colorTheme.v2_border_2};
  }
`;

const StatusBadge = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 8px 16px;
  border-radius: 100px;
  border: none;

  &:hover {
    opacity: 0.8;
  }
`;
